import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Profile.css';

export default function Profile({ employee, onLogout }) {
  const [profile, setProfile]       = useState(null);
  const [salary, setSalary]         = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Change password
  const [showChangePw, setShowChangePw] = useState(false);
  const [oldPw, setOldPw]               = useState('');
  const [newPw, setNewPw]               = useState('');
  const [confirmPw, setConfirmPw]       = useState('');
  const [pwLoading, setPwLoading]       = useState(false);
  const [pwError, setPwError]           = useState('');
  const [pwSuccess, setPwSuccess]       = useState('');

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [profData, salData, attData] = await Promise.all([
        api.getMyProfile(),
        api.getMySalary(month, year),
        api.getMyAttendance(month, year),
      ]);
      setProfile(profData);
      setSalary(salData);
      setAttendance(attData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError(''); setPwSuccess('');
    if (!oldPw)  { setPwError('Enter your current password'); return; }
    if (!newPw)  { setPwError('Enter a new password'); return; }
    if (newPw.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    if (newPw === oldPw)  { setPwError('New password must be different'); return; }

    setPwLoading(true);
    try {
      const res = await fetch('https://fotileams.com/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          current_password: oldPw,
          new_password: newPw,
          employee_id: employee?.employee_id,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setPwError(data.error || 'Failed'); return; }
      setPwSuccess('Password changed successfully!');
      setOldPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setShowChangePw(false); setPwSuccess(''); }, 2000);
    } catch { setPwError('Connection error — try again'); }
    finally { setPwLoading(false); }
  };

  const resetPwForm = () => {
    setShowChangePw(false);
    setPwError(''); setPwSuccess('');
    setOldPw(''); setNewPw(''); setConfirmPw('');
  };

  const initials = employee?.full_name
    ? employee.full_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : '??';

  // ── FIXED: use backend summary (already excludes holidays/leaves) ──
  const summary        = attendance?.summary || {};
  const presentDays    = summary.present || 0;
  const absentDays     = summary.absent  || 0;
  const lateDays       = summary.lates   || 0;
  const holidaysDays   = summary.holidays || 0;
  const onLeaveDays    = summary.on_leave || 0;
  const totalWorking   = presentDays + absentDays;
  const attendanceRate = totalWorking > 0 ? ((presentDays / totalWorking) * 100).toFixed(1) : '0.0';

  const strengthLevel = newPw.length === 0 ? '' : newPw.length < 6 ? 'weak' : newPw.length < 10 ? 'medium' : 'strong';
  const strengthLabel = newPw.length === 0 ? '' : newPw.length < 6 ? '⚠ Too short' : newPw.length < 10 ? '👍 Good' : '💪 Strong';

  const rateColor = parseFloat(attendanceRate) >= 80 ? '#10b981' : parseFloat(attendanceRate) >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="profile-page">

      {/* Hero */}
      <div className="profile-hero">
        <div className="profile-avatar">{initials}</div>
        <div className="profile-name">{employee?.full_name || '—'}</div>
        <div className="profile-dept">
          {profile?.department_name || profile?.department || employee?.department || 'Fotile Pakistan'}
        </div>
        <span className="profile-badge">Active Employee</span>
      </div>

      {/* Employee details */}
      <div className="prof-section-label">Employee Details</div>
      <div className="prof-card">
        {[
          { key: 'Employee ID', val: employee?.employee_id || '—', mono: true },
          { key: 'Department',  val: profile?.department_name || profile?.department || '—' },
          { key: 'Job Title',   val: profile?.job_title || employee?.job_title || '—' },
          { key: 'Location',    val: profile?.location_name || '—' },
          { key: 'Shift',       val: profile?.shift_name || '—' },
          { key: 'Role',        val: employee?.role ? employee.role.charAt(0).toUpperCase() + employee.role.slice(1) : '—' },
          { key: 'Joined',      val: profile?.joining_date ? new Date(profile.joining_date).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
        ].map((r, i) => (
          <div key={i} className="prof-row">
            <span className="prof-key">{r.key}</span>
            <span className={`prof-val ${r.mono ? 'mono' : ''}`}>{r.val}</span>
          </div>
        ))}
      </div>

      {/* This month summary — NEW gradient KPI design */}
      <div className="prof-section-label" style={{ marginTop: 16 }}>
        {now.toLocaleString('default', { month: 'long' })} {year} — Summary
      </div>

      {loading ? (
        <div className="prof-card"><div className="prof-loading">Loading…</div></div>
      ) : (
        <>
          {/* Hero rate card */}
          <div className="prof-rate-hero">
            <div className="prof-rate-left">
              <div className="prof-rate-num" style={{ color: rateColor }}>{attendanceRate}%</div>
              <div className="prof-rate-lbl">Attendance Rate</div>
            </div>
            <div className="prof-rate-ring">
              <svg width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="6" />
                <circle cx="32" cy="32" r="26" fill="none"
                  stroke={rateColor} strokeWidth="6"
                  strokeDasharray={2 * Math.PI * 26}
                  strokeDashoffset={2 * Math.PI * 26 * (1 - Math.min(100, parseFloat(attendanceRate)) / 100)}
                  strokeLinecap="round"
                  transform="rotate(-90 32 32)"
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
              </svg>
            </div>
          </div>

          {/* Gradient KPI grid */}
          <div className="prof-kpi-grid">
            <div className="prof-kpi prof-kpi-green">
              <div className="prof-kpi-num">{presentDays}</div>
              <div className="prof-kpi-lbl">Present</div>
            </div>
            <div className="prof-kpi prof-kpi-red">
              <div className="prof-kpi-num">{absentDays}</div>
              <div className="prof-kpi-lbl">Absent</div>
            </div>
            <div className="prof-kpi prof-kpi-amber">
              <div className="prof-kpi-num">{lateDays}</div>
              <div className="prof-kpi-lbl">Late</div>
            </div>
            {holidaysDays > 0 && (
              <div className="prof-kpi prof-kpi-purple">
                <div className="prof-kpi-num">{holidaysDays}</div>
                <div className="prof-kpi-lbl">Holidays</div>
              </div>
            )}
            {onLeaveDays > 0 && (
              <div className="prof-kpi prof-kpi-blue">
                <div className="prof-kpi-num">{onLeaveDays}</div>
                <div className="prof-kpi-lbl">On Leave</div>
              </div>
            )}
          </div>

          {/* Secondary detail rows */}
          <div className="prof-card">
            <div className="prof-row">
              <span className="prof-key">Total Late Time</span>
              <span className="prof-val">{summary.total_late_minutes > 0 ? `${summary.total_late_minutes}m` : '—'}</span>
            </div>
            <div className="prof-row">
              <span className="prof-key">Salary Deduction</span>
              <span className={`prof-val ${parseFloat(salary?.deduction_percent) > 0 ? 'red' : 'green'}`}>
                {salary?.deduction_percent ? `${salary.deduction_percent}%` : '0%'}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Security — Change Password */}
      <div className="prof-section-label" style={{ marginTop: 16 }}>Security</div>
      {!showChangePw ? (
        <button className="prof-changepw-btn" onClick={() => setShowChangePw(true)}>
          🔐 Change Password
        </button>
      ) : (
        <div className="prof-card prof-changepw-card">
          <div className="prof-changepw-title">Change Password</div>

          {pwError   && <div className="prof-pw-error">{pwError}</div>}
          {pwSuccess && <div className="prof-pw-success">{pwSuccess}</div>}

          <div className="prof-pw-field">
            <label>Current Password</label>
            <input type="password" placeholder="Enter current password"
              value={oldPw} onChange={e => setOldPw(e.target.value)} />
          </div>
          <div className="prof-pw-field">
            <label>New Password</label>
            <input type="password" placeholder="At least 6 characters"
              value={newPw} onChange={e => setNewPw(e.target.value)} />
          </div>
          <div className="prof-pw-field">
            <label>Confirm New Password</label>
            <input type="password" placeholder="Repeat new password"
              value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleChangePassword()} />
          </div>

          {newPw.length > 0 && (
            <div className="prof-pw-strength">
              <div className="prof-pw-bar">
                <div className={`prof-pw-fill prof-pw-${strengthLevel}`}
                  style={{ width: `${Math.min(100, (newPw.length / 12) * 100)}%` }} />
              </div>
              <span className="prof-pw-label">{strengthLabel}</span>
            </div>
          )}

          <div className="prof-pw-btns">
            <button className="prof-pw-cancel" onClick={resetPwForm}>Cancel</button>
            <button className="prof-pw-save" onClick={handleChangePassword} disabled={pwLoading}>
              {pwLoading ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </div>
      )}

      <button className="logout-btn" onClick={onLogout}>Sign Out</button>

    </div>
  );
}